import { DatePicker, Flex, Typography } from "antd";
import dayjs, { Dayjs } from "dayjs";
import React, { useState } from "react";
import i18next from "i18next";
import styled from "styled-components";

import {
  useGetDifferenceReport,
  useGetOnTimeDeliveryReport,
  useGetRealOrderReport,
  useGetBonusOnInvoiceReport,
  useGetOnInvoiceReport,
  useGetNcpCompensationReport,
  useGetMultipackReport,
  useGetCompressedGasProductsReport,
} from "api/reactQuery/queryComponents";
import { dateFormat } from "components/Utils/dayjsUtils";

import { useDownloadReport } from "./useDownloadReport";
import DownloadReportButton from "./DownloadReportButton";

const ButtonsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
`;

const MarketplaceReports: React.FC = () => {
  const [startDate, setStartDate] = useState<Dayjs>(dayjs);
  const [endDate, setEndDate] = useState<Dayjs>(dayjs);

  const { mutate: getOnTimeDeliveryReport, isLoading: onTimeDeliveryLoading } =
    useDownloadReport(
      useGetOnTimeDeliveryReport,
      "OnTimeDelivery_report",
      startDate,
      endDate
    );

  const { mutate: getRealOrderReport, isLoading: realOrderReportsLoading } =
    useDownloadReport(
      useGetRealOrderReport,
      "RealOrder_report",
      startDate,
      endDate
    );

  const { mutate: getDifferenceReport, isLoading: differenceReportLoading } =
    useDownloadReport(
      useGetDifferenceReport,
      "Demand_diff_report",
      startDate,
      endDate
    );

  const {
    mutate: getBonusOnInvoiceReport,
    isLoading: bonusOnInvoiceReportLoading,
  } = useDownloadReport(
    useGetBonusOnInvoiceReport,
    "BonusOnInvoice_report",
    startDate,
    endDate
  );

  const { mutate: getOnInvoiceReport, isLoading: onInvoiceReportLoading } =
    useDownloadReport(
      useGetOnInvoiceReport,
      "OnInvoice_report",
      startDate,
      endDate
    );

  const {
    mutate: getNcpCompensationReport,
    isLoading: ncpCompensationReportLoading,
  } = useDownloadReport(
    useGetNcpCompensationReport,
    "NcpCompensation_report",
    startDate,
    endDate
  );

  const { mutate: getMultipackReport, isLoading: multipackReportLoading } =
    useDownloadReport(
      useGetMultipackReport,
      "Multipack_report",
      startDate,
      endDate
    );

  const {
    mutate: getCompressedGasProductsReport,
    isLoading: compressedGasProductsReportLoading,
  } = useDownloadReport(
    useGetCompressedGasProductsReport,
    "Gas_products_report",
    startDate,
    endDate
  );

  const reportButtons = [
    {
      name: i18next.t("demand_diff_report"),
      onClick: getDifferenceReport,
      isLoading: differenceReportLoading,
    },
    {
      name: i18next.t("on_time_delivery_report"),
      onClick: getOnTimeDeliveryReport,
      isLoading: onTimeDeliveryLoading,
    },
    {
      name: i18next.t("real_order_report"),
      onClick: getRealOrderReport,
      isLoading: realOrderReportsLoading,
    },
    {
      name: i18next.t("bonus_on_invoice_report"),
      onClick: getBonusOnInvoiceReport,
      isLoading: bonusOnInvoiceReportLoading,
    },
    {
      name: i18next.t("on_invoice_report"),
      onClick: getOnInvoiceReport,
      isLoading: onInvoiceReportLoading,
    },
    {
      name: i18next.t("ncp_compensation_report"),
      onClick: getNcpCompensationReport,
      isLoading: ncpCompensationReportLoading,
    },
    {
      name: i18next.t("multipack_report"),
      onClick: getMultipackReport,
      isLoading: multipackReportLoading,
    },
    {
      name: i18next.t("gas_products_report"),
      onClick: getCompressedGasProductsReport,
      isLoading: compressedGasProductsReportLoading,
    },
  ];

  return (
    <>
      <Typography.Title level={3} style={{ marginBottom: 20 }}>
        {i18next.t("marketplace_reports")}
      </Typography.Title>

      <Flex align="center" justify="space-between">
        <div>
          <Typography.Text style={{ marginRight: 10 }}>
            {i18next.t("select_period")}
          </Typography.Text>
          <DatePicker.RangePicker
            format={dateFormat}
            maxDate={dayjs()}
            defaultValue={[startDate, endDate]}
            onChange={(dates) => {
              setStartDate(dates[0]);
              setEndDate(dates[1]);
            }}
          />
        </div>

        <ButtonsContainer>
          {reportButtons.map((button) => (
            <DownloadReportButton
              key={button.name}
              reportName={button.name}
              startDate={startDate}
              endDate={endDate}
              isLoading={button.isLoading}
              onClick={button.onClick}
            />
          ))}
        </ButtonsContainer>
      </Flex>
    </>
  );
};
export default MarketplaceReports;
